import { MarkerType, Position, type Edge, type Node } from 'reactflow'

import { DEGRADED_ARTIFACT_SENTINELS } from './readablePayload'
import type {
  NormalizedToolCall,
  WorkflowControlFlowGraph,
  WorkflowGraphEdgeKind,
  WorkflowGraphNodeData,
  WorkflowPlanPreview,
  WorkflowStepRunView,
} from './workflowTypes'
import { isV2Plan } from './workflowTypes'

// ─── Snake (serpentine) layout ────────────────────────────────────────────────
// Node cards lay out in COLUMNS, snaking: down the first column, up the
// second, down the third… Default is 8 rows per column so a typical workflow
// (≤8 steps) stays ONE vertical column that fit-view scales to the screen
// height; only longer workflows expand horizontally into the snake grid.
// (Figma drew columns of four; product decision: single column up to 8.)
// Odd columns are traversed bottom-to-top.
export const SNAKE_ROWS_PER_COLUMN = 8
// Spec §10: snake density is user-controllable in Settings. A client-side
// preference (localStorage) — layout taste, not tenant data.
const SNAKE_DENSITY_KEY = 'workflow-snake-rows'
export function snakeRowsPerColumn(): number {
  try {
    const parsed = Number(localStorage.getItem(SNAKE_DENSITY_KEY))
    return Number.isFinite(parsed) && parsed >= 2 && parsed <= 8 ? parsed : SNAKE_ROWS_PER_COLUMN
  } catch {
    return SNAKE_ROWS_PER_COLUMN
  }
}
export function setSnakeRowsPerColumn(rows: number): void {
  try {
    localStorage.setItem(SNAKE_DENSITY_KEY, String(rows))
  } catch {
    // Private mode — the preference just won't persist.
  }
}
const SNAKE_COL_WIDTH = 330
const SNAKE_ROW_HEIGHT = 200
const SNAKE_X0 = 80
const SNAKE_Y0 = 80

/** Grid slot for step `index` in the snake layout (pure; unit-tested). */
export function snakePosition(
  index: number,
  rowsPerColumn: number = snakeRowsPerColumn(),
): { x: number; y: number; column: number; row: number } {
  const rows = Math.max(1, rowsPerColumn)
  const column = Math.floor(index / rows)
  const rawRow = index % rows
  // Serpentine: odd columns run bottom→top.
  const row = column % 2 === 1 ? rows - 1 - rawRow : rawRow
  return {
    x: SNAKE_X0 + column * SNAKE_COL_WIDTH,
    y: SNAKE_Y0 + row * SNAKE_ROW_HEIGHT,
    column,
    row,
  }
}

/**
 * Which node handles an edge should use, from the two snake slots. Downward
 * pairs leave the bottom, upward pairs leave the top, column crossings run
 * right→left (or left→right for a backward branch), and a self-edge (retry)
 * loops off the right side. (Handle ids are rendered by WorkflowStepNode:
 * `s-*` sources, `t-*` targets.)
 */
export function snakeEdgeHandles(
  from: { column: number; row: number },
  to: { column: number; row: number },
): { sourceHandle: string; targetHandle: string } {
  if (from.column === to.column && from.row === to.row) {
    return { sourceHandle: 's-right', targetHandle: 't-right' }
  }
  if (from.column !== to.column) {
    return to.column > from.column
      ? { sourceHandle: 's-right', targetHandle: 't-left' }
      : { sourceHandle: 's-left', targetHandle: 't-right' }
  }
  return to.row > from.row
    ? { sourceHandle: 's-bottom', targetHandle: 't-top' }
    : { sourceHandle: 's-top', targetHandle: 't-bottom' }
}

/** Subtle connector styling shared by plan + run edges (thin gray, per design). */
const SNAKE_EDGE_STYLE = { stroke: '#cbd5e1', strokeWidth: 1.5 }

/**
 * Human-friendly labels for the (closed, code-defined) workflow tool set, so the
 * canvas shows "Searched documents" instead of `search_doc_chunks`. The tool set
 * is the same across tenants, so this is generic UI vocabulary (not a
 * tenant-specific assumption). Unknown tools fall back to a prettified name.
 */
const TOOL_LABELS: Record<string, string> = {
  search_doc_chunks: 'Searched documents',
  find_doc_sections: 'Found document sections',
  resolve_canonical: 'Resolved tag / equipment',
  query_historian: 'Queried historian',
  query_datasheet: 'Queried datasheet',
  trace_connections: 'Traced connections',
  trace_cross_sheet: 'Traced across sheets',
  list_control_loops_on_sheet: 'Listed control loops',
  get_sheet: 'Opened P&ID sheet',
  list_board_items: 'Listed board items',
  request_clarification: 'Asked a question',
}

/** Friendly display label for a workflow tool name; prettifies unknown names. */
export function toolDisplayLabel(name: string): string {
  const known = TOOL_LABELS[name]
  if (known) return known
  return (
    name
      .replace(/[_-]+/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\b\w/g, (c) => c.toUpperCase())
      .trim() || name
  )
}

export function buildWorkflowPlanGraph(
  planPreview: WorkflowPlanPreview | null
): {
  nodes: Array<Node<WorkflowGraphNodeData>>
  edges: Edge[]
} {
  // A PRESENT-BUT-EMPTY preview is the case the `!planPreview` guard misses, and it crashed the
  // whole page rather than drawing an empty canvas: a v2 run projects `planPreview` as `{}` (it
  // has a compiled step list, not v1's phase preview), so `planPreview.phases` is `undefined`
  // and `.map` threw inside `WorkflowBuilder`'s `useMemo`. React Router caught it at the route
  // boundary and rendered `InternalServerErrorPage` — the "500 - Internal Server Error" screen
  // reported from dev, which is our own error page and not an HTTP status at all (every API
  // call in those traces returned 200).
  //
  // Reachable whenever a v2 run has NO step rows yet, which is every refused run: the registry
  // declines before any step opens. Read defensively rather than relying on the writer, because
  // rows persisted with `{}` already exist and a deploy cannot go back and fix them.
  const phases = planPreview?.phases
  if (!planPreview || !Array.isArray(phases) || phases.length === 0) {
    return { nodes: [], edges: [] }
  }

  const nodes: Array<Node<WorkflowGraphNodeData>> = []
  const edges: Edge[] = []
  const slots = phases.map((_, index) => snakePosition(index))
  phases.forEach((phase, index) => {
    const id = `draft:${phase.id}`
    const slot = slots[index]
    nodes.push({
      id,
      type: 'workflowStep',
      position: { x: slot.x, y: slot.y },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      data: {
        kind: 'step',
        label: `${phase.title}\ndraft`,
        title: phase.title,
        subtitle: phase.completionCriteria || undefined,
        status: 'draft',
        // A DRAFT HAS NO STEPS, so the phase is the only thing that can speak here — on a v2
        // preview too, where the checkbox REPORTS rather than configures (`hitlReadonly` below).
        // Blanking it for v2 would leave a v2 preview unable to show a blocking card at all.
        // The run view below is where the step, once it exists, takes over.
        hitl: phase.mayAskClarification,
        // Draft nodes are toggleable — the builder wires the checkbox to the
        // persisted per-step override (hitlByStep), same row as the inspector.
        //
        // READ-ONLY for a v2 plan: the engine never consults `hitlByStep`, so an editable
        // toggle would change nothing while looking like it did. On a v2 card the checkbox
        // REPORTS that the step can ask when the data cannot answer.
        hitlReadonly: isV2Plan(planPreview),
        clickable: true,
        draftPhase: phase,
      },
    })

    if (index > 0) {
      const handles = snakeEdgeHandles(slots[index - 1], slot)
      edges.push({
        id: `draft:${planPreview.phases[index - 1]?.id}->${id}`,
        source: `draft:${planPreview.phases[index - 1]?.id}`,
        target: id,
        ...handles,
        type: 'smoothstep',
        style: SNAKE_EDGE_STYLE,
      })
    }
  })

  return { nodes, edges }
}

/**
 * A concise one-line descriptor of what a step did, derived from its (collapsed)
 * tool calls — e.g. "Searched documents · Queried historian", or "Searched
 * documents +2 more". Lets the canvas node read as a clean card with a purpose
 * line instead of a stack of chips; the per-tool detail lives in the chat /
 * node-details dialog. Returns undefined when the step ran no tools.
 */
function describeStepTools(toolCalls: Array<{ name: string }>): string | undefined {
  if (toolCalls.length === 0) return undefined
  const labels = [...new Set(toolCalls.map((tc) => toolDisplayLabel(tc.name)))]
  if (labels.length <= 2) return labels.join(' · ')
  return `${labels.slice(0, 2).join(' · ')} +${labels.length - 2} more`
}

/**
 * Run graph in the snake layout. One card per step (the step's output/evidence
 * lives in the node-properties panel, not a separate output node). Body text
 * prefers the plan phase's completion criteria (the closest human description
 * of the step), falling back to a summary of the tools the step ran.
 */
/**
 * Sentinels a DEGRADED step's artifact carries — imported from `readablePayload.ts`, which owns the
 * list because the panel must hide the SAME keys this grading reads. One copy on the frontend side, so
 * a fifth brake cannot be added to the pill and forgotten in the panel.
 *
 * The runner writes each of these when it substituted a fallback artifact for the step's real output —
 * quota brake, soft time guard, KG precondition, model provider, error-continued — and none of them
 * adds a blockedClaim, so a step cut off mid-work rendered a clean green Success. Live run 1579b339:
 * `gather-historian` ended `fallback_output kind=quota_stop` after 7 successful calls and showed
 * GREEN, while `safety-triage` — which retrieved cleanly and honestly recorded what it could not
 * establish — showed amber. The board was telling the user the opposite of the truth.
 *
 * TWIN of the backend's `classifyFallbackOutput`
 * (`backend/web/src/kg-agent/workflows/workflow-ai-events.ts`), where a new sentinel is born; there is
 * no shared module across that boundary. The drift is silent and one-directional: a sentinel added
 * backend-side and not here gets a `fallback_output` event and a GREEN pill. (It already happened
 * once — `stepErrorContinued`.)
 */


/**
 * A completed step whose ARTIFACT says it found less than a clean Success implies — render
 * "Success · open questions" instead.
 *
 * TWO signals, both RUNNER-WRITTEN and both read from the artifact the user is actually shown, so the
 * pill cannot contradict the panel below it:
 *   1. `blockedClaims` — what the step could not establish. The engine writes these as well as the
 *      model: an unfilled DECLARED output field and a dropped retrieval leg both land here.
 *   2. a degraded-fallback sentinel — the runner replaced the step's output after a brake fired.
 *
 * DELIBERATELY NOT a third signal on `output.status`. That field is MODEL-authored against a
 * GENERATOR-authored schema, so matching it against a vocabulary of process words (`incomplete`,
 * `partial`, …) is a free-text heuristic over model output — and one that cannot be replayed against
 * real traffic from a dev box today (no artifact bodies in the logs), i.e. it could only be argued
 * about, never falsified. It also collides with the plant's own language: the shipped templates tell
 * steps to emit `supported | refuted | unresolved | blocked` for hypotheses, and "blocked" is a real
 * P&ID condition. The case it was added for — live run c9ba23a7, `resolve-reflux-tags` shipping three
 * of four declared fields as `"unknown"` — is now covered where the SCHEMA is known: the runner
 * records those fields as a blocked claim (`withUnfilledFieldsBlockedClaim`), which arrives through
 * signal 1 with no vocabulary involved.
 *
 * NOTE: the return value is a SUPERSET of `WorkflowStepStatus` — it adds the display-only
 * 'completed_with_unknowns' (a run-level status), matching the deliberately-loose
 * `WorkflowGraphNodeData.status: string` ('ready'/'draft'/'terminal' are also virtual). Consumers match
 * by string, not by the step union.
 */
function displayStatusFor(step: WorkflowStepRunView): string {
  if (step.status !== 'completed') return step.status
  const output = step.output as Record<string, unknown> | null | undefined
  if (!output) return step.status
  const blocked = output.blockedClaims
  if (Array.isArray(blocked) && blocked.length > 0) return 'completed_with_unknowns'
  if (DEGRADED_ARTIFACT_SENTINELS.some((key) => output[key] === true)) {
    return 'completed_with_unknowns'
  }
  return step.status
}

export function buildWorkflowGraph(
  steps: WorkflowStepRunView[],
  planPreview?: WorkflowPlanPreview | null
): {
  nodes: Array<Node<WorkflowGraphNodeData>>
  edges: Edge[]
} {
  const nodes: Array<Node<WorkflowGraphNodeData>> = []
  const edges: Edge[] = []
  const phaseById = new Map(
    (planPreview?.phases ?? []).map((phase) => [phase.id, phase])
  )
  // Latest run row per declarative stepId (a re-visited step keeps its
  // newest — ONE card per declarative step by design, so a card keeps its
  // identity across revisits instead of the canvas regrowing per attempt).
  // "Newest" is decided by the rows themselves (stepIndex, then startedAt),
  // never by array order — the API mostly returns oldest-first, but a
  // mid-update refetch must not flash a stale row onto a planned card.
  const newerRow = (a: WorkflowStepRunView, b: WorkflowStepRunView): WorkflowStepRunView => {
    if ((a.stepIndex ?? 0) !== (b.stepIndex ?? 0)) {
      return (a.stepIndex ?? 0) > (b.stepIndex ?? 0) ? a : b
    }
    return (a.startedAt ?? '') >= (b.startedAt ?? '') ? a : b
  }
  const latestByStepId = new Map<string, WorkflowStepRunView>()
  for (const step of steps) {
    const prev = latestByStepId.get(step.stepId)
    latestByStepId.set(step.stepId, prev ? newerRow(step, prev) : step)
  }

  // Figma run view: EVERY planned phase is a card from the moment the run
  // starts — done cards green, the executing card amber, the rest "Ready" —
  // instead of cards trickling in one-by-one as their step rows appear.
  // Extra step rows the plan didn't predict (decision-routed revisits) are
  // appended after the planned cards. Node ids key on the DECLARATIVE step id
  // so a card keeps its identity across ready → running → done.
  type Card = {
    key: string
    title: string
    phase: WorkflowPlanPreview['phases'][number] | null
    step: WorkflowStepRunView | null
  }
  const phases = planPreview?.phases ?? []
  const cards: Card[] = phases.length
    ? phases.map((phase) => ({
        key: phase.id,
        title: latestByStepId.get(phase.id)?.title ?? phase.title,
        phase,
        step: latestByStepId.get(phase.id) ?? null,
      }))
    : []
  const planned = new Set(cards.map((card) => card.key))
  for (const step of latestByStepId.values()) {
    if (planned.has(step.stepId)) continue
    cards.push({
      key: step.stepId,
      title: step.title,
      phase: phaseById.get(step.stepId) ?? null,
      step,
    })
  }

  const slots = cards.map((_, index) => snakePosition(index))
  cards.forEach((card, index) => {
    const slot = slots[index]
    const stepNodeId = `step:${card.key}`
    const step = card.step
    // Tool calls render INLINE inside the step node (friendly chips), not as
    // separate nodes — see WorkflowStepNode. Collapsed for a clean canvas.
    const toolCalls = step
      ? collapseToolCallEvents(step.toolCalls ?? []).map((toolCall, toolIndex) =>
          normalizeToolCall(toolCall, toolIndex, step)
        )
      : []
    const phase = card.phase
    nodes.push({
      id: stepNodeId,
      type: 'workflowStep',
      position: { x: slot.x, y: slot.y },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      data: {
        kind: 'step',
        label: `${card.title}\n${step ? step.status : 'ready'}`,
        title: card.title,
        subtitle: phase?.completionCriteria || describeStepTools(toolCalls) || undefined,
        // Planned-but-not-run cards read "Ready"; degraded completions read
        // "Success · open questions" instead of a clean Success.
        status: step ? displayStatusFor(step) : 'ready',
        // ON A V2 RUN, THE STEP'S OWN KIND WEARS THE BADGE — NOT THE V1 DRAFT'S FIELD.
        //
        // `mayAskClarification` is a V1 CANONICAL-DRAFT field, matched onto a v2 card by position.
        // The v1 draft marks its first five phases `true`, so every early v2 card inherited the row
        // and the canvas showed "Human in the loop" on eight cards that cannot pause — the engine
        // never reads `mayAskClarification`, and a v2 run stops only on a `hitl` step. It read as
        // "this whole workflow needs babysitting" and none of it was true.
        //
        // THREE THINGS THIS HAS TO GET RIGHT, each of which an earlier draft got wrong:
        //
        //   BOTH kinds block. `clarification` is the v1 step kind; a v2 period card / elicit park
        //   persists `kind: 'hitl'` (`doc-plan-generator.ts`, `v1-plan-adapter.ts`). Testing only
        //   `clarification` silently un-badges the one v2 card whose whole job is to block, which
        //   is the failure `plan-preview.ts` already documents.
        //
        //   NOT gated on `isV2Plan`. That helper recognises a v2 preview by what it CANNOT carry
        //   (no operators, no budgets) — so a v1 plan that simply omits both is classified v2, and
        //   a v1 `clarification` park would lose its badge. Two canvas tests hold exactly that
        //   shape. The step's own kind answers this without needing to know which engine wrote it.
        //
        //   only when there IS a step. A plan preview has none, so a step-kind test answers false
        //   for every card and a preview could never show a blocking card; the phase is the only
        //   source a preview has, and reading it there is what that v1 field was written for.
        //
        // AND THE KIND IS NOT ENOUGH ON ITS OWN — `status` carries the fourth case, which a
        // kind-only rule silently regressed. `workflow-runner.service.ts`'s `pauseForClarification`
        // parks a v1 AGENTIC step by setting `run.status` and `stepRun.status` to
        // `waiting_for_user` and leaving `kind` as `agentic`; an embedded clarification never
        // becomes a `clarification` step. Before this change that card wore the badge via the
        // `|| phase?.mayAskClarification` arm, and a kind-only test drops it — on the worst
        // possible card, one that is not merely able to pause but is paused and waiting on this
        // user right now.
        //
        // So the rule is: IS IT BLOCKED, or is it the KIND OF STEP THAT BLOCKS. `status` is
        // engine-agnostic in exactly the way `kind` was meant to be, and a completed v2 gather is
        // neither — so this costs none of the over-badging the cases above fix.
        hitl: step
          ? step.status === 'waiting_for_user' ||
            step.kind === 'hitl' ||
            step.kind === 'clarification'
          : phase?.mayAskClarification === true,
        // Toggleable even on a run view: hitlByStep configures FUTURE runs
        // (and the engine's review gate) — locking it after the first run
        // made the checkbox permanently dead for any workflow that ever ran.
        // A v2 plan is the exception: nothing reads the override, so it reports
        // rather than configures.
        hitlReadonly: isV2Plan(planPreview),
        isAgent: step ? step.kind === 'agentic' : true,
        isVerify: step?.kind === 'verify',
        clickable: true,
        ...(step ? { step } : { draftPhase: phase ?? undefined }),
        toolCalls,
      },
    })

    if (index > 0) {
      const previous = cards[index - 1]
      const handles = snakeEdgeHandles(slots[index - 1], slot)
      edges.push({
        id: `step:${previous.key}->${stepNodeId}`,
        source: `step:${previous.key}`,
        target: stepNodeId,
        ...handles,
        type: 'smoothstep',
        style: SNAKE_EDGE_STYLE,
      })
    }
  })

  return { nodes, edges }
}

/**
 * Persisted step toolCalls are an event log: one 'running' entry pushed when
 * a call starts and a separate terminal 'ok'/'error' entry when it finishes,
 * so rendering one node per entry shows a single call multiple times (with a
 * zombie 'running' node on completed steps). Collapse each start/finish pair
 * into one per-call entry — matched by toolCallId when present, falling back
 * to FIFO pairing per tool name for legacy runs persisted without ids. The
 * terminal entry wins; the start entry contributes its args preview. Runner
 * marker records (kind: 'workflow_*', e.g. soft-timeout breadcrumbs) are not
 * tool calls and are dropped; unmatched 'running' entries (genuinely in
 * flight) and non-record entries pass through unchanged.
 */
export function collapseToolCallEvents(values: unknown[]): unknown[] {
  const collapsed: unknown[] = []
  const pendingById = new Map<string, number>()
  const pendingByName = new Map<string, number[]>()
  for (const value of values) {
    if (!isRecord(value)) {
      collapsed.push(value)
      continue
    }
    if (typeof value.kind === 'string' && value.kind.startsWith('workflow_')) {
      continue
    }
    const id = firstString(value.toolCallId, value.id, value.callId)
    const name = firstString(value.toolName, value.name, value.tool)
    const status = normalizeToolCallStatus(
      firstString(value.status, value.state)
    )
    if (status === 'running') {
      // Interim status events repeat 'running' for an already-started call;
      // fold them into the pending entry instead of appending another one.
      // Matched by id when present; a legacy entry without an id is treated
      // as interim only when it carries no args while the pending start does
      // (start events carry the args preview, interim status events don't) —
      // so two id-less concurrent calls to an args-less tool stay separate.
      let existingIndex: number | undefined
      if (id) {
        existingIndex = pendingById.get(id)
      } else if (
        name &&
        firstPresent(value.args, value.input, value.argsPreview) === undefined
      ) {
        const queue = pendingByName.get(name)
        const candidate = queue?.[queue.length - 1]
        const start = candidate === undefined ? undefined : collapsed[candidate]
        if (
          isRecord(start) &&
          firstPresent(start.args, start.input, start.argsPreview) !== undefined
        ) {
          existingIndex = candidate
        }
      }
      if (existingIndex !== undefined) {
        const start = collapsed[existingIndex]
        collapsed[existingIndex] = isRecord(start)
          ? { ...start, ...value }
          : value
        continue
      }
      const index = collapsed.length
      collapsed.push(value)
      if (id) {
        pendingById.set(id, index)
      } else if (name) {
        const queue = pendingByName.get(name) ?? []
        queue.push(index)
        pendingByName.set(name, queue)
      }
      continue
    }
    const startIndex = id
      ? pendingById.get(id)
      : name
        ? pendingByName.get(name)?.shift()
        : undefined
    if (startIndex === undefined) {
      collapsed.push(value)
      continue
    }
    if (id) pendingById.delete(id)
    const start = collapsed[startIndex]
    collapsed[startIndex] = isRecord(start) ? { ...start, ...value } : value
  }
  return collapsed
}

export function normalizeToolCall(
  raw: unknown,
  index: number,
  step: WorkflowStepRunView
): NormalizedToolCall {
  const record = isRecord(raw) ? raw : {}
  const name = firstString(record.toolName, record.name, record.tool) ?? 'tool'
  const status = normalizeToolCallStatus(
    firstString(record.status, record.state)
  )

  return {
    id:
      firstString(record.toolCallId, record.id, record.callId) ??
      `${step.id}:tool:${index}`,
    name,
    status,
    input: firstPresent(
      record.args,
      record.input,
      record.inputPreview,
      record.argsPreview
    ),
    output: firstPresent(
      record.result,
      record.output,
      record.outputPreview,
      record.resultPreview
    ),
    error: firstPresent(record.error, record.errorMessage, record.errorPreview),
    raw,
    step,
  }
}

function normalizeToolCallStatus(
  rawStatus: string | undefined
): 'ok' | 'error' | 'running' {
  return rawStatus === 'ok' || rawStatus === 'success' || rawStatus === 'completed'
    ? 'ok'
    : rawStatus === 'error' || rawStatus === 'failed'
      ? 'error'
      : 'running'
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value
  }
  return undefined
}

function firstPresent(...values: unknown[]): unknown {
  return values.find((value) => value !== undefined && value !== null)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// --- control-flow graph (read-only flowchart) ---

const CF_CHILD_Y_GAP = 96

/** Per-edge-kind styling for the control-flow flowchart. */
const CF_EDGE_STYLE: Record<WorkflowGraphEdgeKind, { color: string; dashed?: boolean }> = {
  next: { color: '#64748b' },
  branch: { color: '#d97706', dashed: true },
  fanout: { color: '#2563eb' },
  retry: { color: '#dc2626', dashed: true },
}

const cfNodeId = (id: string): string => `cf:${id}`

/**
 * Map a backend control-flow projection (workflow-graph.ts) into ReactFlow
 * nodes + edges that show the ACTUAL branching structure — decision branches,
 * fanout, and bounded retry — rather than a naive linear chain. Read-only: nodes
 * are non-clickable and carry no run payload (canvas authoring stays v2).
 *
 * Top-level nodes sit on the SAME snake grid as the Outline view, so Detail
 * fits the screen the same way instead of running one long vertical lane.
 * Fanout children stack in a side lane just past the grid's last column.
 */
export function buildControlFlowGraph(graph: WorkflowControlFlowGraph | null): {
  nodes: Array<Node<WorkflowGraphNodeData>>
  edges: Edge[]
} {
  if (!graph) return { nodes: [], edges: [] }

  const topLevel = graph.nodes.filter((node) => !node.parentId)
  // Resolve density ONCE per build: snakePosition's default re-reads
  // localStorage per node otherwise (N+1 reads for a pure layout pass).
  const rowsPerColumn = snakeRowsPerColumn()
  const slotById = new Map<string, ReturnType<typeof snakePosition>>()
  topLevel.forEach((node, index) => slotById.set(node.id, snakePosition(index, rowsPerColumn)))
  const gridColumns = Math.ceil(topLevel.length / rowsPerColumn)
  const sideLaneX = SNAKE_X0 + gridColumns * SNAKE_COL_WIDTH
  const childSeq = new Map<string, number>()

  const nodes: Array<Node<WorkflowGraphNodeData>> = graph.nodes.map((node) => {
    let slot = slotById.get(node.id)
    if (!slot && node.parentId) {
      // Stack fanout children in the side lane, level with their parent.
      const seq = childSeq.get(node.parentId) ?? 0
      childSeq.set(node.parentId, seq + 1)
      const parent = slotById.get(node.parentId)
      slot = {
        x: sideLaneX,
        y: (parent?.y ?? SNAKE_Y0) + seq * CF_CHILD_Y_GAP,
        column: gridColumns,
        row: (parent?.row ?? 0) + seq,
      }
      slotById.set(node.id, slot)
    }
    const { x, y } = slot ?? { x: SNAKE_X0, y: SNAKE_Y0 }
    return {
      id: cfNodeId(node.id),
      type: 'workflowStep',
      position: { x, y },
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
      data: {
        kind: 'step',
        label: `${node.title}\n${node.kind}`,
        title: node.title,
        subtitle: node.terminal ? `${node.kind} · terminal` : node.kind,
        status: node.terminal ? 'terminal' : node.kind,
        isVerify: node.kind === 'verify',
        isAgent: node.kind === 'agentic',
        clickable: false,
      },
    }
  })

  const edges: Edge[] = graph.edges.map((edge, index) => {
    const style = CF_EDGE_STYLE[edge.kind] ?? CF_EDGE_STYLE.next
    const from = slotById.get(edge.from)
    const to = slotById.get(edge.to)
    return {
      id: `cf:${edge.from}->${edge.to}:${edge.kind}:${index}`,
      source: cfNodeId(edge.from),
      target: cfNodeId(edge.to),
      ...(from && to ? snakeEdgeHandles(from, to) : {}),
      type: 'smoothstep',
      label: edge.label,
      labelBgPadding: [4, 2] as [number, number],
      markerEnd: { type: MarkerType.ArrowClosed, color: style.color },
      style: {
        stroke: style.color,
        strokeWidth: 1.5,
        ...(style.dashed ? { strokeDasharray: '6 4' } : {}),
      },
    }
  })

  return { nodes, edges }
}
